import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react'

import { IconButton } from './IconButton'

/**
 * L'ALERTE UNIQUE (LOT-H9).
 *
 * ══════════════════════════════════════════════════════════════════════════
 * POURQUOI CE FICHIER EXISTE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * L'audit visuel du 2026-08 (`docs/design/AUDIT-VISUEL-2026-08.md` §6.1) a
 * compté **6 façons d'avertir l'utilisateur** :
 *
 *   ① bandeau ambre à halo (poste téléphonique en panne, `AppShell`)
 *   ② toast rouge plein (seuils de `/monitoring`)
 *   ③ encadré bleu à bordure 1 px (`/settings` → Permissions)
 *   ④ encadré bleu teinté en modale (import d'utilisateurs)
 *   ⑤ ligne inline préfixée d'un emoji ⚠️ / ℹ️ (partout)
 *   ⑥ bandeau blanc crème de la scène v2 (zone GELÉE, §9 — hors périmètre)
 *
 * …auxquelles la recette a ajouté un septième cas non inventorié :
 * `/admin/all-users` avertit en **`orange-*`**, une couleur qui n'est même pas
 * un token du produit (§1.2 : avertissement = `warning`, base ambre).
 *
 * Ce composant est le gabarit unique qui les remplace. Un seul dessin, quatre
 * TONS — et le ton dit le métier, pas l'envie d'être vu.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * LE TON EST UN RÔLE (DESIGN-SYSTEM §1.2)
 * ══════════════════════════════════════════════════════════════════════════
 *
 * | Ton       | Token     | Quand                                              |
 * |-----------|-----------|----------------------------------------------------|
 * | `info`    | `primary` | Contexte, aide, rappel de format. Aucun jugement.  |
 * | `success` | `success` | Une opération a abouti.                            |
 * | `warning` | `warning` | Seuil franchi, quota proche, solde bas, à surveiller |
 * | `danger`  | `danger`  | **Échec ou erreur bloquante**, et rien d'autre.    |
 *
 * La frontière `warning` / `danger` est celle que DA-001 a fait tomber :
 * `/monitoring` annonçait en ROUGE un solde Twilio bas — un état à surveiller,
 * pas une panne. Un rouge qui ne signale pas un échec finit par ne plus rien
 * signaler du tout.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * CE QUI PORTE LA COULEUR, ET CE QUI NE LA PORTE PAS
 * ══════════════════════════════════════════════════════════════════════════
 *
 * La teinte vit dans **la bordure, le fond translucide et l'icône**. Le titre
 * est en `foreground`, la description en `foreground-secondary` — comme partout
 * ailleurs dans le produit. Les 6 variantes d'origine coloraient au contraire le
 * TEXTE (`text-blue-400`, `text-amber-200`, `text-orange-400`…), ce qui donnait
 * six contrastes différents pour la même fonction et rendait le bandeau ambre
 * illisible dès qu'on y posait un bouton.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * DEUX SURFACES, UN SEUL GABARIT
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `surface="inline"` (défaut) : l'alerte est DANS le flux — bandeau de la zone
 * de notification, encadré d'aide dans un formulaire. Fond translucide, elle se
 * pose sur le fond de page.
 *
 * `surface="floating"` : l'alerte est une notification éphémère, posée
 * au-dessus du contenu. Même dessin, mais un fond **opaque** (`*-950/95`) et une
 * ombre : une bulle translucide au-dessus d'un tableau est illisible. C'est la
 * seule différence — il n'y a pas « un composant Toast » à côté de « un
 * composant Alert », il y a un gabarit et deux surfaces.
 */

export type AlertTone = 'info' | 'success' | 'warning' | 'danger'
export type AlertSurface = 'inline' | 'floating'

interface ToneStyle {
  border: string
  inline: string
  floating: string
  icon: string
  Icon: LucideIcon
  /** `alert` = assertif (le lecteur d'écran interrompt), `status` = poli. */
  role: 'alert' | 'status'
}

/**
 * Les classes sont ÉCRITES EN ENTIER, jamais composées à l'exécution
 * (`bg-${tone}-500/10` ne serait jamais généré par Tailwind, qui scanne le
 * source — le piège documenté dans `StatCard.tsx`).
 */
const TONES: Record<AlertTone, ToneStyle> = {
  info: {
    border: 'border-primary-500/30',
    inline: 'bg-primary-500/10',
    floating: 'bg-primary-950/95',
    icon: 'text-primary-400',
    Icon: Info,
    role: 'status',
  },
  success: {
    border: 'border-success-500/30',
    inline: 'bg-success-500/10',
    floating: 'bg-success-950/95',
    icon: 'text-success-400',
    Icon: CheckCircle2,
    role: 'status',
  },
  warning: {
    border: 'border-warning-500/40',
    inline: 'bg-warning-500/10',
    floating: 'bg-warning-950/95',
    icon: 'text-warning-400',
    Icon: AlertTriangle,
    role: 'alert',
  },
  danger: {
    border: 'border-danger-500/40',
    inline: 'bg-danger-500/10',
    floating: 'bg-danger-950/95',
    icon: 'text-danger-400',
    Icon: XCircle,
    role: 'alert',
  },
}

export interface AlertProps {
  tone?: AlertTone
  surface?: AlertSurface
  /** Le fait, en une phrase. C'est la seule partie obligatoire. */
  title: ReactNode
  /** Le détail : dénominateur, fenêtre, conséquence. Rendu sous le titre. */
  children?: ReactNode
  /** Remplace l'icône du ton. `null` retire l'icône. */
  icon?: LucideIcon | null
  /** Classes ajoutées à l'icône — `animate-spin` pour un état en cours, et guère plus. */
  iconClassName?: string
  /** UNE action, et une seule — au-delà, ce n'est plus une alerte mais un panneau. */
  action?: ReactNode
  /** Rend la croix de fermeture. Absent ⇒ pas de croix. */
  onDismiss?: () => void
  /** Nom accessible de la croix. Défaut : « Fermer » via i18next. */
  dismissLabel?: string
  /** Force la politesse ARIA quand le ton ne la décrit pas (toast de succès…). */
  role?: 'alert' | 'status'
  className?: string
}

export function Alert({
  tone = 'info',
  surface = 'inline',
  title,
  children,
  icon,
  iconClassName = '',
  action,
  onDismiss,
  dismissLabel,
  role,
  className = '',
}: AlertProps) {
  const { t } = useTranslation('common')
  const style = TONES[tone]
  // `icon === null` retire l'icône ; `undefined` prend celle du ton.
  const Icon = icon === null ? null : (icon ?? style.Icon)

  return (
    <div
      role={role ?? style.role}
      className={[
        'flex w-full items-start gap-3 rounded-card border px-4 py-3',
        style.border,
        surface === 'floating'
          ? `${style.floating} shadow-large backdrop-blur-xl`
          : style.inline,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {Icon && (
        <Icon
          className={`mt-0.5 h-[18px] w-[18px] shrink-0 ${style.icon} ${iconClassName}`.trim()}
          aria-hidden="true"
        />
      )}

      {/* `min-w-0` : sans lui, un message long POUSSE l'action et la croix hors
          du bandeau au lieu de se replier — le défaut que le PageHeader de H2 a
          déjà eu à corriger sur sa propre rangée. */}
      <div className="min-w-0 flex-1">
        <div className="text-t3 text-foreground">{title}</div>
        {children && <div className="mt-0.5 text-t5 text-foreground-secondary">{children}</div>}
      </div>

      {action && <div className="shrink-0">{action}</div>}

      {onDismiss && (
        <IconButton
          icon={X}
          size="sm"
          label={dismissLabel ?? t('actions.close')}
          onClick={onDismiss}
          className="-mr-1 shrink-0"
        />
      )}
    </div>
  )
}

export default Alert
